/**
 * Etiquetas de los ejes y de los sectores del Panel de Estadísticas.
 *
 * POR QUÉ EXISTE: los desbordes que reportó el cliente no son de CSS, son de texto que no cabe
 * donde se pinta. Recharts dibuja cada etiqueta tal cual se la den, y si es larga se solapa con la
 * siguiente o se sale del SVG, donde la tarjeta la recorta. Aquí vive la decisión de qué texto
 * cabe; los widgets sólo la aplican.
 *
 * Vive aparte porque así se puede probar: en jsdom un gráfico de Recharts mide 0×0 y no sirve para
 * comprobar nada de esto.
 */
import { parseAnalyticsDate, truncate } from './analyticsDashboardFormat';

/** Cuántos caracteres caben en el eje de categorías (160 px a 11 px de tipografía). */
export const AXIS_LABEL_MAX_CHARS = 18;

/**
 * Nombre legible de una entidad (agente, formulario, canal) a partir de su id.
 *
 * OJO CON LA RESERVA: `useAnalyticsDataEnrichment` devuelve **el id** cuando no conoce la entidad
 * (`userMap.get(id) || id`), no `undefined`. Por eso un `getUserName(id) || 'Usuario…'` nunca
 * entra, y en el eje acababa pintándose el ObjectId crudo. Hay que comparar contra el id.
 */
export const resolveEntityName = (id, lookup, prefix = 'Usuario') => {
  const rawId = id == null ? '' : String(id);
  if (!rawId) return prefix;
  const resolved = typeof lookup === 'function' ? lookup(rawId) : null;
  if (resolved && String(resolved) !== rawId) return String(resolved);
  return `${prefix} ${rawId.slice(-6)}`;
};

/**
 * Fecha corta para un eje temporal: `2026-08-12` → `12 ago`.
 *
 * La fecha completa ocupa el triple y, con un punto por día, las etiquetas se montan unas sobre
 * otras. El año no aporta: el período elegido ya está en la barra de filtros, y el tooltip da la
 * fecha completa.
 */
export const axisDateTick = (value) => {
  if (!value) return '';
  const date = parseAnalyticsDate(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

/**
 * Recorta una etiqueta de categoría al ancho del eje. El nombre completo sigue en el tooltip.
 *
 * Recharts llama a `tickFormatter(value, index)` (ver `CartesianAxis.js`): el segundo argumento es
 * el índice del tick, no un `maxChars`. Tomarlo como tal hacía que el eje pintara "...", "B...",
 * "Ar..." — de ahí que la función sólo reciba `value` y use siempre `AXIS_LABEL_MAX_CHARS`.
 */
export const axisCategoryTick = (value) => truncate(value == null ? '' : String(value), AXIS_LABEL_MAX_CHARS);

/**
 * Etiqueta de un sector: SÓLO el porcentaje.
 *
 * Con el nombre dentro, un sector como "Facebook Ad 000348 9%" se dibuja fuera del círculo y la
 * tarjeta lo recorta a media palabra. El nombre está en la leyenda y en el tooltip, que es donde
 * cabe.
 */
export const slicePercentLabel = ({ percent } = {}) => {
  const value = Number(percent);
  if (!Number.isFinite(value) || value <= 0.04) return '';
  return `${(value * 100).toFixed(0)}%`;
};

/** Separación entre el borde del sector y su etiqueta de porcentaje (Recharts usa 20 por defecto). */
export const PIE_LABEL_OFFSET = 26;

const RADIAN = Math.PI / 180;

/**
 * Punto donde va la etiqueta de un sector, a `offset` px del borde exterior, y hacia qué lado se
 * ancla el texto para que crezca hacia afuera del círculo.
 *
 * Recharts coloca las etiquetas a 20 px del borde y no deja cambiarlo cuando `label` es una
 * función; con porcentajes pegados al sector se veían sin aire. Aquí vive la geometría para poder
 * probarla sin montar el gráfico (en jsdom mide 0×0).
 */
export const pieLabelPoint = ({ cx, cy, midAngle, outerRadius }, offset = PIE_LABEL_OFFSET) => {
  const radius = Number(outerRadius) + offset;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const textAnchor = Math.abs(x - cx) < 0.5 ? 'middle' : x > cx ? 'start' : 'end';
  return { x, y, textAnchor };
};

